import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Server, Globe, Gauge, ShieldBan, Lock, Plus, Trash2,
  CheckCircle2, RefreshCw, KeyRound, Flame, StopCircle,
} from "lucide-react";

const S = `${process.env.REACT_APP_BACKEND_URL}/api/v1/settings`;

const PROVIDER_TYPES = ["SES", "SENDGRID", "RESEND", "M365", "GWS", "SIMULATION"];
const CRED_FIELDS = {
  SES: [["accessKeyId", "Access Key ID"], ["secretAccessKey", "Secret Access Key"], ["region", "Region (e.g. us-east-1)"]],
  SENDGRID: [["apiKey", "SendGrid API Key"]],
  RESEND: [["apiKey", "Resend API Key (re_...)"]],
  M365: [["tenantId", "Tenant ID"], ["clientId", "Client ID"], ["clientSecret", "Client Secret VALUE"]],
  GWS: [["serviceAccountJson", "Service Account JSON"]],
  SIMULATION: [],
};

const SUBTABS = [
  { id: "providers", label: "Providers", icon: Server },
  { id: "domains", label: "Sending Domains", icon: Globe },
  { id: "throttling", label: "Throttling", icon: Gauge },
  { id: "suppression", label: "Suppression", icon: ShieldBan },
  { id: "security", label: "Security", icon: Lock },
];

function Banner({ msg }) {
  if (!msg) return null;
  return <div className={`set-banner ${msg.kind}`} data-testid="settings-banner">{msg.text}</div>;
}

const WARMUP_TIP =
  "Your domain is currently warming up to protect your sender reputation. Sending limits will increase automatically each day.";

/** Subtle warm-up status: 🟢 Active, or 🟡 Warming with day + daily-volume bar. */
export function WarmupBadge({ warmup }) {
  if (!warmup) return null;
  if (!warmup.warming) {
    return (
      <span className="wu-badge wu-active" data-testid="warmup-badge" title="Domain is fully warmed — no sending cap.">
        <span className="wu-dot" /> Active
      </span>
    );
  }
  const pct = warmup.cap ? Math.min(100, Math.round((warmup.used / warmup.cap) * 100)) : 0;
  return (
    <span className="wu-badge wu-warming" data-testid="warmup-badge" title={WARMUP_TIP}>
      <span className="wu-dot" />
      <span className="wu-txt">Warming · Day {warmup.day}/{warmup.totalDays}</span>
      <span className="wu-bar"><span className="wu-fill" style={{ width: `${pct}%` }} /></span>
      <span className="wu-count">{warmup.used}/{warmup.cap} today</span>
    </span>
  );
}

/* ---------------- Providers ---------------- */
function Providers({ notify }) {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("SES");
  const [name, setName] = useState("");
  const [priority, setPriority] = useState(100);
  const [costPer1k, setCostPer1k] = useState(0);
  const [dailyLimit, setDailyLimit] = useState(50000);
  const [creds, setCreds] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${S}/providers`);
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true);
    try {
      await axios.post(`${S}/providers`, {
        name, type, priority: Number(priority), costPer1k: Number(costPer1k),
        dailyLimit: Number(dailyLimit), credentials: creds,
      });
      notify("success", `Provider "${name}" added — credentials encrypted (AES-256-GCM).`);
      setName(""); setCreds({});
      load();
    } catch (e) {
      notify("error", e?.response?.data?.message || e.message);
    } finally { setBusy(false); }
  };

  const del = async (id, nm) => {
    await axios.delete(`${S}/providers/${id}`).catch((e) => notify("error", e.message));
    notify("success", `Removed "${nm}".`);
    load();
  };

  return (
    <div className="cols set-cols">
      <div className="panel" data-testid="add-provider-panel">
        <div className="p-head"><Plus size={15} /><h3>Add provider</h3></div>
        <div className="p-body">
          <div className="field">
            <label>Type</label>
            <select data-testid="provider-type-select" value={type} onChange={(e) => { setType(e.target.value); setCreds({}); }}>
              {PROVIDER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>Name</label>
            <input data-testid="provider-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. ses-us-east-1" />
          </div>
          {CRED_FIELDS[type].map(([k, lbl]) => (
            <div className="field" key={k}>
              <label>{lbl}</label>
              {k === "serviceAccountJson" ? (
                <textarea data-testid={`cred-${k}`} rows={4} value={creds[k] || ""} onChange={(e) => setCreds({ ...creds, [k]: e.target.value })} placeholder="{ paste JSON }" />
              ) : (
                <input data-testid={`cred-${k}`} type={k.toLowerCase().includes("secret") || k === "apiKey" ? "password" : "text"} value={creds[k] || ""} onChange={(e) => setCreds({ ...creds, [k]: e.target.value })} placeholder={lbl} />
              )}
            </div>
          ))}
          <div className="row3">
            <div className="field"><label>Priority</label><input data-testid="provider-priority-input" type="number" value={priority} onChange={(e) => setPriority(e.target.value)} /></div>
            <div className="field"><label>Cost /1k (¢)</label><input data-testid="provider-cost-input" type="number" value={costPer1k} onChange={(e) => setCostPer1k(e.target.value)} /></div>
            <div className="field"><label>Daily limit</label><input data-testid="provider-limit-input" type="number" value={dailyLimit} onChange={(e) => setDailyLimit(e.target.value)} /></div>
          </div>
          <button className="btn" data-testid="add-provider-btn" disabled={busy || !name} onClick={add}><Plus size={15} /> {busy ? "Saving…" : "Add provider"}</button>
          {type === "M365" && <p className="note">M365 send uses the tenant credentials in the engine environment. Store them here for the record; ensure the env secret is the client secret <b>Value</b>.</p>}
          {type === "RESEND" && <p className="note">Set <code>RESEND_API_KEY</code> in the engine environment. The key stored here is encrypted for reference. From-address is specified per-send.</p>}
          {type === "SES" && <p className="note">Set <code>AWS_ACCESS_KEY_ID</code>, <code>AWS_SECRET_ACCESS_KEY</code>, and <code>AWS_REGION</code> in the engine environment. Credentials here are encrypted for reference.</p>}
        </div>
      </div>

      <div className="panel" data-testid="providers-list-panel">
        <div className="p-head"><Server size={15} /><h3>Configured providers</h3><span className="hint">encrypted credentials</span></div>
        <div className="scroll">
          <table className="tbl">
            <thead><tr><th>Name</th><th>Type</th><th>Priority</th><th>Cost/1k</th><th>Health</th><th>Creds</th><th></th></tr></thead>
            <tbody data-testid="providers-settings-tbody">
              {rows.length === 0 && <tr><td colSpan={7} className="empty">No providers.</td></tr>}
              {rows.map((p) => (
                <tr key={p.id} data-testid={`provider-row-${p.name}`}>
                  <td className="mono-strong">{p.name}</td>
                  <td>{p.type}</td>
                  <td>{p.priority}</td>
                  <td>{p.costPer1k}¢</td>
                  <td><span className={`badge ${p.healthStatus === "HEALTHY" ? "b-delivered" : "b-bounced"}`}>{p.healthStatus}</span></td>
                  <td>{p.credentialsConfigured ? <KeyRound size={13} color="var(--green)" /> : "—"}</td>
                  <td><button className="icon-btn" data-testid={`delete-provider-${p.name}`} onClick={() => del(p.id, p.name)}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Domains ---------------- */
function Domains({ notify }) {
  const [rows, setRows] = useState([]);
  const [domain, setDomain] = useState("");
  const [selectors, setSelectors] = useState("s1");
  const [dmarcMode, setDmarcMode] = useState("MONITOR");
  const [returnPath, setReturnPath] = useState("");

  const load = useCallback(async () => setRows((await axios.get(`${S}/domains`)).data), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try {
      await axios.post(`${S}/domains`, { domain, dkimSelectors: selectors.split(",").map((s) => s.trim()).filter(Boolean), dmarcMode, customReturnPath: returnPath || undefined });
      notify("success", `Domain "${domain}" added — run Verify to check SPF/DKIM/DMARC.`);
      setDomain(""); setReturnPath(""); load();
    } catch (e) { notify("error", e?.response?.data?.message || e.message); }
  };
  const verify = async (id, dm) => {
    try {
      const { data } = await axios.put(`${S}/domains/${id}/verify`);
      const kind = data.verificationStatus === "VALID" ? "success" : "warn";
      notify(kind, `${dm}: ${data.verificationStatus} — SPF ${data.posture.spf}, DKIM ${data.posture.dkim}, DMARC ${data.posture.dmarc}`);
      load();
    } catch (e) { notify("error", e?.response?.data?.message || e.message); }
  };
  const del = async (id) => { await axios.delete(`${S}/domains/${id}`); load(); };

  const startWarmup = async (id, dm) => {
    try {
      const { data } = await axios.put(`${S}/domains/${id}/warmup`, { action: "start" });
      notify("success", `Warm-up started for ${dm} — Day 1 cap: 50 emails. Increases daily over 10 days.`);
      load();
    } catch (e) { notify("error", e?.response?.data?.message || e.message); }
  };
  const stopWarmup = async (id, dm) => {
    try {
      await axios.put(`${S}/domains/${id}/warmup`, { action: "stop" });
      notify("warn", `Warm-up stopped for ${dm}.`);
      load();
    } catch (e) { notify("error", e?.response?.data?.message || e.message); }
  };

  const st = (s) => <span className={`badge ${s === "VALID" ? "b-delivered" : s === "PENDING" ? "b-deferred" : "b-bounced"}`}>{s}</span>;

  return (
    <div className="cols set-cols">
      <div className="panel" data-testid="add-domain-panel">
        <div className="p-head"><Plus size={15} /><h3>Add sending domain</h3></div>
        <div className="p-body">
          <div className="field"><label>Domain</label><input data-testid="domain-input" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="mail.acme.com" /></div>
          <div className="field"><label>DKIM selectors (comma-sep)</label><input data-testid="domain-selectors-input" value={selectors} onChange={(e) => setSelectors(e.target.value)} placeholder="s1, s2" /></div>
          <div className="field"><label>Custom return-path (optional)</label><input data-testid="domain-returnpath-input" value={returnPath} onChange={(e) => setReturnPath(e.target.value)} placeholder="bounce.acme.com" /></div>
          <div className="field"><label>DMARC enforcement</label>
            <select data-testid="domain-dmarc-select" value={dmarcMode} onChange={(e) => setDmarcMode(e.target.value)}>
              <option value="MONITOR">MONITOR (observe only)</option>
              <option value="STRICT">STRICT (block unaligned)</option>
            </select>
          </div>
          <button className="btn" data-testid="add-domain-btn" disabled={!domain} onClick={add}><Plus size={15} /> Add domain</button>
        </div>
      </div>

      <div className="panel" data-testid="domains-list-panel">
        <div className="p-head"><Globe size={15} /><h3>Domains</h3><span className="hint">DNS auth posture</span></div>
        <div className="scroll">
          <table className="tbl">
            <thead><tr><th>Domain</th><th>SPF</th><th>DKIM</th><th>DMARC</th><th>Verified</th><th>Warm-up</th><th></th></tr></thead>
            <tbody data-testid="domains-settings-tbody">
              {rows.length === 0 && <tr><td colSpan={7} className="empty">No domains.</td></tr>}
              {rows.map((d) => (
                <tr key={d.id} data-testid={`domain-row-${d.domain}`}>
                  <td className="mono-strong">{d.domain}</td>
                  <td>{st(d.spfStatus)}</td><td>{st(d.dkimStatus)}</td><td>{st(d.dmarcStatus)}</td>
                  <td>{st(d.verificationStatus)}</td>
                  <td><WarmupBadge warmup={d.warmup} /></td>
                  <td className="actions">
                    <button className="icon-btn" title="Verify DNS" data-testid={`verify-domain-${d.domain}`} onClick={() => verify(d.id, d.domain)}><CheckCircle2 size={14} /></button>
                    {!d.warmup?.startedAt ? (
                      <button className="icon-btn" title="Start 10-day warm-up" data-testid={`warmup-start-${d.domain}`} onClick={() => startWarmup(d.id, d.domain)} style={{ color: "var(--yellow)" }}><Flame size={14} /></button>
                    ) : d.warmup?.warming ? (
                      <button className="icon-btn" title="Stop warm-up" data-testid={`warmup-stop-${d.domain}`} onClick={() => stopWarmup(d.id, d.domain)} style={{ color: "var(--red)" }}><StopCircle size={14} /></button>
                    ) : null}
                    <button className="icon-btn" data-testid={`delete-domain-${d.domain}`} onClick={() => del(d.id)}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Throttling ---------------- */
function Throttling({ notify }) {
  const [rows, setRows] = useState([]);
  const [recipientDomain, setRd] = useState("");
  const [maxPerHour, setMph] = useState(200);
  const [maxPerMinute, setMpm] = useState("");
  const [isDefault, setDef] = useState(false);

  const load = useCallback(async () => setRows((await axios.get(`${S}/throttling`)).data), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await axios.post(`${S}/throttling`, { recipientDomain: isDefault ? "*" : recipientDomain, maxPerHour: Number(maxPerHour), maxPerMinute: maxPerMinute ? Number(maxPerMinute) : undefined, isDefault });
      notify("success", "Throttling rule saved — pushed to Redis for all workers.");
      setRd(""); setMpm(""); load();
    } catch (e) { notify("error", e?.response?.data?.message || e.message); }
  };
  const del = async (id) => { await axios.delete(`${S}/throttling/${id}`); load(); };

  return (
    <div className="cols set-cols">
      <div className="panel" data-testid="add-throttle-panel">
        <div className="p-head"><Plus size={15} /><h3>Add rule</h3></div>
        <div className="p-body">
          <label className="chk" data-testid="throttle-default-check"><input type="checkbox" checked={isDefault} onChange={(e) => setDef(e.target.checked)} /> Default (catch-all) rule</label>
          {!isDefault && <div className="field"><label>Recipient domain</label><input data-testid="throttle-domain-input" value={recipientDomain} onChange={(e) => setRd(e.target.value)} placeholder="gmail.com" /></div>}
          <div className="row3">
            <div className="field"><label>Max / hour</label><input data-testid="throttle-hour-input" type="number" value={maxPerHour} onChange={(e) => setMph(e.target.value)} /></div>
            <div className="field"><label>Max / min (opt)</label><input data-testid="throttle-minute-input" type="number" value={maxPerMinute} onChange={(e) => setMpm(e.target.value)} placeholder="—" /></div>
          </div>
          <button className="btn" data-testid="save-throttle-btn" disabled={!isDefault && !recipientDomain} onClick={save}><Plus size={15} /> Save rule</button>
        </div>
      </div>

      <div className="panel" data-testid="throttle-list-panel">
        <div className="p-head"><Gauge size={15} /><h3>Throttling rules</h3><span className="hint">per recipient domain</span></div>
        <div className="scroll">
          <table className="tbl">
            <thead><tr><th>Domain</th><th>Per hour</th><th>Per minute</th><th>Default</th><th></th></tr></thead>
            <tbody data-testid="throttle-tbody">
              {rows.length === 0 && <tr><td colSpan={5} className="empty">No rules — built-in reputation-safe defaults apply.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`throttle-row-${r.recipientDomain}`}>
                  <td className="mono-strong">{r.isDefault ? "★ default (*)" : r.recipientDomain}</td>
                  <td>{r.maxPerHour}</td><td>{r.maxPerMinute ?? "—"}</td>
                  <td>{r.isDefault ? "yes" : "—"}</td>
                  <td><button className="icon-btn" data-testid={`delete-throttle-${r.recipientDomain}`} onClick={() => del(r.id)}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Suppression ---------------- */
function Suppression({ notify }) {
  const [cfg, setCfg] = useState(null);
  const [list, setList] = useState([]);
  const [email, setEmail] = useState("");

  const load = useCallback(async () => {
    const [c, l] = await Promise.all([axios.get(`${S}/suppression/config`), axios.get(`${S}/suppression/list?limit=50`)]);
    setCfg(c.data); setList(l.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async () => {
    await axios.post(`${S}/suppression/rules`, {
      softBounceThreshold: Number(cfg.softBounceThreshold), complaintAction: cfg.complaintAction,
      suppressionTtlDays: Number(cfg.suppressionTtlDays), hardBounceTtlDays: Number(cfg.hardBounceTtlDays),
    });
    notify("success", "Suppression policy updated.");
    load();
  };
  const add = async () => { await axios.post(`${S}/suppression/list`, { email, reason: "MANUAL" }); setEmail(""); notify("success", `Suppressed ${email}.`); load(); };
  const del = async (id) => { await axios.delete(`${S}/suppression/list/${id}`); load(); };

  if (!cfg) return null;
  return (
    <div className="cols set-cols">
      <div className="panel" data-testid="suppression-config-panel">
        <div className="p-head"><ShieldBan size={15} /><h3>Bounce / complaint policy</h3></div>
        <div className="p-body">
          <div className="field"><label>Soft-bounce threshold</label><input data-testid="soft-threshold-input" type="number" value={cfg.softBounceThreshold} onChange={(e) => setCfg({ ...cfg, softBounceThreshold: e.target.value })} /></div>
          <div className="field"><label>Complaint action</label>
            <select data-testid="complaint-action-select" value={cfg.complaintAction} onChange={(e) => setCfg({ ...cfg, complaintAction: e.target.value })}>
              <option value="suppress_immediately">Suppress immediately</option>
              <option value="warn">Warn only</option>
              <option value="ignore">Ignore</option>
            </select>
          </div>
          <div className="row3">
            <div className="field"><label>Soft TTL (days)</label><input data-testid="suppression-ttl-input" type="number" value={cfg.suppressionTtlDays} onChange={(e) => setCfg({ ...cfg, suppressionTtlDays: e.target.value })} /></div>
            <div className="field"><label>Hard TTL (0=never)</label><input data-testid="hardbounce-ttl-input" type="number" value={cfg.hardBounceTtlDays} onChange={(e) => setCfg({ ...cfg, hardBounceTtlDays: e.target.value })} /></div>
          </div>
          <button className="btn" data-testid="save-suppression-config-btn" onClick={saveCfg}><CheckCircle2 size={15} /> Save policy</button>
          <div className="field" style={{ marginTop: 18 }}><label>Manually suppress an address</label><input data-testid="suppress-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@domain.com" /></div>
          <button className="btn ghost" data-testid="add-suppression-btn" disabled={!email} onClick={add}><Plus size={15} /> Suppress address</button>
        </div>
      </div>

      <div className="panel" data-testid="suppression-list-panel">
        <div className="p-head"><ShieldBan size={15} /><h3>Suppression list</h3><span className="hint">global blocklist</span></div>
        <div className="scroll">
          <table className="tbl">
            <thead><tr><th>Email</th><th>Reason</th><th>Source</th><th></th></tr></thead>
            <tbody data-testid="suppression-settings-tbody">
              {list.length === 0 && <tr><td colSpan={4} className="empty">Empty.</td></tr>}
              {list.map((s) => (
                <tr key={s.id}>
                  <td className="mono-strong">{s.email}</td>
                  <td><span className="badge b-suppressed">{s.reason}</span></td>
                  <td>{s.source}</td>
                  <td><button className="icon-btn" data-testid={`delete-suppression-${s.id.slice(0, 6)}`} onClick={() => del(s.id)}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Security ---------------- */
function Security({ notify }) {
  const [cfg, setCfg] = useState(null);
  const [secrets, setSecrets] = useState({ ses: "", sendgrid: "", m365: "" });
  const load = useCallback(async () => setCfg((await axios.get(`${S}/security`)).data), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const body = {
      apiRateLimitPerMin: Number(cfg.apiRateLimitPerMin) || undefined,
      dataRetentionDays: Number(cfg.dataRetentionDays) || undefined,
    };
    const set = Object.fromEntries(Object.entries(secrets).filter(([, v]) => v));
    if (Object.keys(set).length) body.webhookSigningSecrets = set;
    await axios.post(`${S}/security`, body);
    notify("success", "Security & compliance settings saved (secrets encrypted).");
    setSecrets({ ses: "", sendgrid: "", m365: "" });
    load();
  };
  if (!cfg) return null;
  return (
    <div className="panel narrow" data-testid="security-panel">
      <div className="p-head"><Lock size={15} /><h3>Security & compliance</h3></div>
      <div className="p-body">
        <div className="row3">
          <div className="field"><label>API rate limit /min</label><input data-testid="api-rate-input" type="number" value={cfg.apiRateLimitPerMin} onChange={(e) => setCfg({ ...cfg, apiRateLimitPerMin: e.target.value })} /></div>
          <div className="field"><label>Data retention (days)</label><input data-testid="retention-input" type="number" value={cfg.dataRetentionDays} onChange={(e) => setCfg({ ...cfg, dataRetentionDays: e.target.value })} /></div>
        </div>
        <p className="note">Webhook signing secrets {cfg.webhookSigningSecrets ? "are configured (encrypted)." : "not set."} Enter to set/rotate:</p>
        <div className="field"><label>SES / SNS secret</label><input data-testid="secret-ses-input" type="password" value={secrets.ses} onChange={(e) => setSecrets({ ...secrets, ses: e.target.value })} placeholder="••••" /></div>
        <div className="field"><label>SendGrid secret</label><input data-testid="secret-sendgrid-input" type="password" value={secrets.sendgrid} onChange={(e) => setSecrets({ ...secrets, sendgrid: e.target.value })} placeholder="••••" /></div>
        <div className="field"><label>M365 clientState</label><input data-testid="secret-m365-input" type="password" value={secrets.m365} onChange={(e) => setSecrets({ ...secrets, m365: e.target.value })} placeholder="••••" /></div>
        <button className="btn" data-testid="save-security-btn" onClick={save}><Lock size={15} /> Save settings</button>
      </div>
    </div>
  );
}

export default function Settings() {
  const [tab, setTab] = useState("providers");
  const [msg, setMsg] = useState(null);
  const notify = useCallback((kind, text) => {
    setMsg({ kind, text });
    setTimeout(() => setMsg(null), 5000);
  }, []);

  return (
    <div data-testid="settings-view">
      <div className="set-tabs" data-testid="settings-subtabs">
        {SUBTABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} className={`set-tab ${tab === t.id ? "active" : ""}`} data-testid={`subtab-${t.id}`} onClick={() => setTab(t.id)}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>
      <Banner msg={msg} />
      {tab === "providers" && <Providers notify={notify} />}
      {tab === "domains" && <Domains notify={notify} />}
      {tab === "throttling" && <Throttling notify={notify} />}
      {tab === "suppression" && <Suppression notify={notify} />}
      {tab === "security" && <Security notify={notify} />}
    </div>
  );
}
